import React, { Component } from "react";
import { Result, Button } from "antd";
import type { ErrorBoundaryProps, ErrorBoundaryState } from "../types";

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      console.group("🚨 Error Boundary Caught Error");
      console.error("Error:", error);
      console.error("Error Message:", error.message);
      console.error("Error Stack:", error.stack);
      console.error("Component Stack:", errorInfo.componentStack);
      console.groupEnd();

      // Log additional details in development
      const isDev =
        typeof import.meta !== "undefined" &&
        import.meta.env &&
        import.meta.env.DEV;
      if (isDev) {
        console.warn(
          "⚠️ This error was caught by ErrorBoundary. Check the logs above for details.",
        );
      }
    } catch (e) {
      // Fallback to basic error logging if grouping fails
      console.error("Error caught by boundary:", error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDev =
        typeof import.meta !== "undefined" &&
        import.meta.env &&
        import.meta.env.DEV;
      const error = this.state.error;

      return (
        <div style={{ padding: "20px" }}>
          <Result
            status="error"
            title="Something went wrong"
            subTitle={
              isDev && error
                ? `${error.name}: ${error.message}`
                : "An unexpected error occurred. Please try refreshing the page."
            }
            extra={
              <Button type="primary" onClick={() => window.location.reload()}>
                Refresh Page
              </Button>
            }
          />
          {isDev && error && (
            <div
              style={{
                marginTop: "20px",
                padding: "16px",
                backgroundColor: "#fff1f0",
                border: "1px solid #ffccc7",
                borderRadius: "4px",
                maxWidth: "800px",
                margin: "20px auto",
              }}
            >
              <h3 style={{ color: "#cf1322", marginBottom: "12px" }}>
                Error Details (Development Only)
              </h3>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontSize: "12px",
                  backgroundColor: "#fff",
                  padding: "12px",
                  borderRadius: "4px",
                  overflow: "auto",
                  maxHeight: "400px",
                }}
              >
                {error.stack || error.message}
              </pre>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
